/**
 * Página de selección de planes v2 - Experiencia de checkout con comparativa visual
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getPlans,
  updateSubscription,
  createSubscription,
  activateMercadoPagoSubscription,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PlanCardsGrid from '../components/PlanCardsGrid';
import FeatureComparisonTable from '../components/FeatureComparisonTable';
import AddonsSection from '../components/AddonsSection';
import EnterpriseLeadForm from '../components/EnterpriseLeadForm';
import HelpIcon from '../components/help/HelpIcon';
import { hasCustomPricing } from '../utils/plans';
import './PlanSelectionPage.css';

// Map i18n language to default currency
const LOCALE_CURRENCY = {
  es: 'ARS',
  en: 'USD',
  pt: 'BRL',
};

function PlanSelectionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { organization, user } = useAuth();
  const { getCurrentPlan, subscription } = useSubscription();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showEnterpriseContact, setShowEnterpriseContact] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState(LOCALE_CURRENCY[i18n.language] || 'USD');
  const [showComparison, setShowComparison] = useState(false);

  // Obtener datos de navegación (suscripción actual si existe)
  const currentSubscription = location.state?.currentSubscription;
  const isUpgrade = !!currentSubscription;

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPlans();
      const sortedPlans = (response.plans || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setPlans(sortedPlans);

      // Pre-seleccionar plan actual o profesional por defecto
      if (currentSubscription) {
        setSelectedPlanId(currentSubscription.planId);
      } else {
        const professionalPlan = sortedPlans.find((p) => p.planId === 'professional');
        if (professionalPlan) {
          setSelectedPlanId(professionalPlan.planId);
        }
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      setError(t('planSelection.errorLoadingPlans'));
    } finally {
      setLoading(false);
    }
  }, [currentSubscription, t]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedPlanId) {
      setError(t('planSelection.selectPlanError'));
      return;
    }

    // Enterprise plan requires sales contact, not direct checkout
    const plan = plans.find((p) => p.planId === selectedPlanId);
    if (hasCustomPricing(plan, currency)) {
      setShowEnterpriseContact(true);
      return;
    }

    // Si es el mismo plan, no hacer nada
    if (currentSubscription && selectedPlanId === currentSubscription.planId) {
      navigate('/subscription');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      if (isUpgrade) {
        // Downgrade to free — direct update, no payment needed
        if (selectedPlanId === 'free') {
          await updateSubscription(currentSubscription.subscriptionId, {
            planId: selectedPlanId,
            billingInterval: billingCycle,
            currency,
          });
          navigate('/subscription', { state: { success: true } });
          return;
        }

        // Upgrade to paid plan — update subscription then redirect to MP checkout
        await updateSubscription(currentSubscription.subscriptionId, {
          planId: selectedPlanId,
          billingInterval: billingCycle,
          currency,
          paymentMethodId: 'pending',
        });

        const payerEmail = user?.username || organization?.contactEmail;
        const { init_point } = await activateMercadoPagoSubscription(
          currentSubscription.subscriptionId,
          payerEmail
        );

        if (init_point) {
          window.location.href = init_point;
          return;
        }
      } else {
        const trialEnabled = selectedPlanId === 'professional';
        await createSubscription({
          organizationId: organization.organizationId,
          planId: selectedPlanId,
          billingInterval: billingCycle,
          currency,
          trialEnabled,
          userEmail: user?.username || null,
          userName: organization?.name || user?.username?.split('@')[0] || null,
        });
      }

      navigate('/subscription', { state: { success: true } });
    } catch (err) {
      console.error('Error confirming plan:', err);
      setError(
        err.response?.data?.message ||
          t(isUpgrade ? 'planSelection.errorUpdating' : 'planSelection.errorCreating')
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/subscription');
  };

  const getCurrencySymbol = (curr) => {
    switch (curr) {
      case 'ARS':
        return 'AR$';
      case 'BRL':
        return 'R$';
      default:
        return '$';
    }
  };

  const symbol = getCurrencySymbol(currency);

  const selectedPlan = plans.find((p) => p.planId === selectedPlanId);
  const isEnterprisePlan = hasCustomPricing(selectedPlan, currency);

  // Compute display price based on billing cycle and currency
  const getDisplayPrice = (plan) => {
    const pricing = plan?.pricing?.[currency] || plan?.pricing?.USD;
    if (!pricing || hasCustomPricing(plan, currency)) return null;
    const monthly = pricing.monthly;
    if (monthly === null || monthly === undefined) return null;
    if (billingCycle === 'annual') {
      return pricing.annual != null ? pricing.annual : Math.round(monthly * 10);
    }
    return monthly;
  };

  // Calculate actual annual discount percentage
  const annualDiscountPercent = (() => {
    const paidPlan = plans.find((p) => {
      const pricing = p?.pricing?.[currency] || p?.pricing?.USD;
      return (
        pricing && !hasCustomPricing(p, currency) && pricing.monthly > 0 && pricing.annual != null
      );
    });
    if (!paidPlan) return 20; // 20% default for v2
    const pricing = paidPlan.pricing[currency] || paidPlan.pricing.USD;
    return Math.round((1 - pricing.annual / (pricing.monthly * 12)) * 100);
  })();

  // Detect same-plan selection
  const contextPlanId = getCurrentPlan();
  const isSamePlan = currentSubscription
    ? selectedPlanId === currentSubscription.planId
    : !!(selectedPlanId && selectedPlanId.toLowerCase() === contextPlanId?.toLowerCase());

  const currentPlan = currentSubscription
    ? plans.find((p) => p.planId === currentSubscription.planId)
    : null;

  if (loading) {
    return (
      <div className="plan-selection-page">
        <div className="loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="plan-selection-page">
      {/* Header */}
      <div className="plan-selection-header">
        <button type="button" className="back-button" onClick={handleCancel}>
          ← {t('common.back')}
        </button>
        <div className="header-content">
          <h1>
            {isUpgrade ? t('planSelection.changePlanTitle') : t('planSelection.selectPlanTitle')}{' '}
            <HelpIcon topicKey="subscription.tiers" />
          </h1>
          <p className="subtitle">{t('planSelection.subtitle')}</p>
        </div>
      </div>

      {/* Currency + Billing Toggle Row */}
      <div className="plan-selection-controls">
        <div className="currency-selector">
          {['ARS', 'USD', 'BRL'].map((curr) => (
            <button
              key={curr}
              type="button"
              className={`currency-option ${currency === curr ? 'active' : ''}`}
              onClick={() => setCurrency(curr)}
            >
              {getCurrencySymbol(curr)} {curr}
            </button>
          ))}
        </div>

        <div className="billing-toggle-container">
          <HelpIcon topicKey="subscription.billingCycle" />
          <button
            type="button"
            className={`billing-toggle-option ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            {t('planSelection.monthly')}
          </button>
          <button
            type="button"
            className={`billing-toggle-option ${billingCycle === 'annual' ? 'active' : ''}`}
            onClick={() => setBillingCycle('annual')}
          >
            {t('planSelection.annual')}
            <span className="billing-toggle-badge">
              {t('planSelection.annualDiscount', { percent: annualDiscountPercent })}
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid + Checkout */}
      <div className="plans-checkout-container">
        <PlanCardsGrid
          plans={plans}
          selectedPlanId={selectedPlanId}
          onSelect={handlePlanSelect}
          currentPlanId={currentPlan?.planId}
          billingCycle={billingCycle}
          currency={currency}
        />

        {/* Checkout Summary */}
        <div className="checkout-summary">
          <div className="summary-card">
            <h3>{t('planSelection.orderSummary')}</h3>

            {selectedPlan ? (
              <>
                <div className="summary-item">
                  <span className="summary-label">{t('planSelection.selectedPlan')}:</span>
                  <span className="summary-value">
                    {t(
                      `planSelection.plans.${selectedPlan.planId}.name`,
                      selectedPlan.displayName || selectedPlan.name
                    )}
                  </span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">{t('planSelection.billingCycle')}:</span>
                  <span className="summary-value">
                    {billingCycle === 'annual'
                      ? t('planSelection.annual')
                      : t('planSelection.monthly')}
                  </span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">{t('planSelection.currencyLabel')}:</span>
                  <span className="summary-value">{currency}</span>
                </div>

                {selectedPlan.planId === 'professional' && !currentSubscription && (
                  <div className="summary-trial">
                    <div className="trial-icon">🎉</div>
                    <div>
                      <div className="trial-title">{t('planSelection.freeTrial')}</div>
                      <div className="trial-text">{t('planSelection.trialDescription')}</div>
                    </div>
                  </div>
                )}

                <div className="summary-divider"></div>

                {isSamePlan ? (
                  <div className="summary-total same-plan-summary">
                    <span className="total-label">{t('planSelection.alreadyOnThisPlan')}</span>
                  </div>
                ) : (
                  <>
                    <div className="summary-total">
                      <span className="total-label">
                        {currentSubscription && selectedPlan.planId === 'professional'
                          ? t('planSelection.dueToday')
                          : t('planSelection.total')}
                        :
                      </span>
                      <span className="total-value">
                        {(() => {
                          const price = getDisplayPrice(selectedPlan);
                          if (price !== null) {
                            return billingCycle === 'annual'
                              ? `${symbol}${price}/${t('planSelection.year')}`
                              : `${symbol}${price}/${t('planSelection.month')}`;
                          }
                          return isEnterprisePlan
                            ? t('planSelection.contactUs')
                            : t('planSelection.free');
                        })()}
                      </span>
                    </div>

                    {selectedPlan.planId === 'professional' && !currentSubscription && (
                      <p className="summary-note">{t('planSelection.trialNote')}</p>
                    )}
                  </>
                )}
              </>
            ) : (
              <p className="no-selection">{t('planSelection.noSelection')}</p>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="checkout-actions">
              <button type="button" className="btn btn-ghost btn-block" onClick={handleCancel}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={handleConfirm}
                disabled={!selectedPlanId || processing || isSamePlan}
                aria-disabled={!selectedPlanId || processing || isSamePlan}
              >
                {processing
                  ? t('planSelection.processing')
                  : isEnterprisePlan
                    ? t('planSelection.contactSales')
                    : isUpgrade
                      ? t('planSelection.confirmChange')
                      : t('planSelection.confirmPlan')}
              </button>
            </div>

            {/* Enterprise Lead Form Modal */}
            {showEnterpriseContact && (
              <div className="modal-overlay" onClick={() => setShowEnterpriseContact(false)}>
                <div
                  className="modal-content modal-content--wide"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <h2>{t('planSelection.enterpriseContactTitle')}</h2>
                    <button
                      type="button"
                      className="close-btn"
                      onClick={() => setShowEnterpriseContact(false)}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="modal-body">
                    <p style={{ marginBottom: 'var(--space-md)', color: 'var(--text-secondary)' }}>
                      {t('planSelection.enterpriseContactMessage')}
                    </p>
                    <EnterpriseLeadForm onClose={() => setShowEnterpriseContact(false)} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compare Features Toggle */}
      <div className="comparison-toggle-container">
        <button
          type="button"
          className="comparison-toggle-btn"
          onClick={() => setShowComparison(!showComparison)}
        >
          {showComparison ? t('planSelection.hideComparison') : t('planSelection.showComparison')}
        </button>
      </div>

      {/* Feature Comparison Table */}
      {showComparison && (
        <div className="comparison-section">
          <FeatureComparisonTable plans={plans} currentPlanId={currentPlan?.planId} />
        </div>
      )}

      {/* Add-ons Section */}
      <div className="addons-container">
        <AddonsSection
          subscriptionId={subscription?.subscriptionId}
          currentPlanId={selectedPlanId || currentPlan?.planId}
          currency={currency}
        />
      </div>
    </div>
  );
}

export default PlanSelectionPage;
